/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {scheduleMicroTask} from '../util';
import {AnimationPlayer} from './animation_player';
export class AnimationGroupPlayer implements AnimationPlayer {
private _onDoneFns: Function[] = [];
private _onStartFns: Function[] = [];
private _finished = false;
private _started = false;
private _destroyed = false;
private _onDestroyFns: Function[] = [];
public parentPlayer: AnimationPlayer|null = null;
/**
 * @param {?} _players
 */
constructor(private _players: AnimationPlayer[]) {
    let count = 0;
    const total = this._players.length;
    if (total == 0) {
      scheduleMicroTask(() => this._onFinish());
    } else {
      this._players.forEach(player => {
        player.parentPlayer = this;
        player.onDone(() => {
          if (++count >= total) {
            this._onFinish();
          }
        });
      });
    }
  }
/**
 * @return {?}
 */
private _onFinish() {
    if (!this._finished) {
      this._finished = true;
      this._onDoneFns.forEach(fn => fn());
      this._onDoneFns = [];
    }
  }
/**
 * @return {?}
 */
init(): void { this._players.forEach(player => player.init()); }
/**
 * @param {?} fn
 * @return {?}
 */
onStart(fn: () => void): void { this._onStartFns.push(fn); }
/**
 * @param {?} fn
 * @return {?}
 */
onDone(fn: () => void): void { this._onDoneFns.push(fn); }
/**
 * @param {?} fn
 * @return {?}
 */
onDestroy(fn: () => void): void { this._onDestroyFns.push(fn); }
/**
 * @return {?}
 */
hasStarted() { return this._started; }
/**
 * @return {?}
 */
play() {
    if (!this.parentPlayer) {
      this.init();
    }
    if (!this.hasStarted()) {
      this._onStartFns.forEach(fn => fn());
      this._onStartFns = [];
      this._started = true;
    }
    this._players.forEach(player => player.play());
  }
/**
 * @return {?}
 */
pause(): void { this._players.forEach(player => player.pause()); }
/**
 * @return {?}
 */
restart(): void { this._players.forEach(player => player.restart()); }
/**
 * @return {?}
 */
finish(): void {
    this._onFinish();
    this._players.forEach(player => player.finish());
  }
/**
 * @return {?}
 */
destroy(): void {
    if (!this._destroyed) {
      this._onFinish();
      this._players.forEach(player => player.destroy());
      this._destroyed = true;
      this._onDestroyFns.forEach(fn => fn());
      this._onDestroyFns = [];
    }
  }
/**
 * @return {?}
 */
reset(): void {
    this._players.forEach(player => player.reset());
    this._destroyed = false;
    this._finished = false;
    this._started = false;
  }
/**
 * @param {?} p
 * @return {?}
 */
setPosition(p: number): void {
    this._players.forEach(player => { player.setPosition(p); });
  }
/**
 * @return {?}
 */
getPosition(): number {
    let /** @type {?} */ min = 0;
    this._players.forEach(player => {
      const /** @type {?} */ p = player.getPosition();
      min = Math.min(p, min);
    });
    return min;
  }
/**
 * @return {?}
 */
get players(): AnimationPlayer[] { return this._players; }
}

function AnimationGroupPlayer_tsickle_Closure_declarations() {
/** @type {?} */
AnimationGroupPlayer.prototype._onDoneFns;
/** @type {?} */
AnimationGroupPlayer.prototype._onStartFns;
/** @type {?} */
AnimationGroupPlayer.prototype._finished;
/** @type {?} */
AnimationGroupPlayer.prototype._started;
/** @type {?} */
AnimationGroupPlayer.prototype._destroyed;
/** @type {?} */
AnimationGroupPlayer.prototype._onDestroyFns;
/** @type {?} */
AnimationGroupPlayer.prototype.parentPlayer;
/** @type {?} */
AnimationGroupPlayer.prototype._players;
}

